// Conversations this tab marked read or unread whose job has not broadcast yet. The inbox
// badges and the list are read from the replica, which may not have the change, so both
// apply these marks until the broadcast settles them. Kept in sessionStorage so they
// survive a reload of the tab. Expired marks are dropped.
export const PENDING_MARK_TTL_MS = 5 * 60 * 1000

const STORAGE_KEY = 'teaminbox_pending_marks'

const loadMarks = () => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}')

    return { reads: new Map(stored.reads || []), unreads: new Map(stored.unreads || []) }
  } catch (error) {
    return { reads: new Map(), unreads: new Map() }
  }
}

const { reads: pendingReads, unreads: pendingUnreads } = loadMarks()

const saveMarks = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ reads: [...pendingReads], unreads: [...pendingUnreads] }))
  } catch (error) {
    // without storage the marks only last until the tab reloads
  }
}

const prune = (marks, now) => {
  marks.forEach((mark, contactId) => {
    if (now - mark.at > PENDING_MARK_TTL_MS) {
      marks.delete(contactId)
    }
  })
}

const pruneMarks = now => {
  prune(pendingReads, now)
  prune(pendingUnreads, now)
  saveMarks()
}

const contactCount = (row, contactId) => {
  const contactUnreadKey = `unread_contact_${contactId}`

  return Number(row?.[contactUnreadKey] || 0)
}

// Every unread of these conversations in `inboxIds` is being cleared
export function addPendingReads (contactIds, inboxIds, now = Date.now()) {
  contactIds.forEach(contactId => {
    pendingReads.set(contactId, { inboxIds, at: now })
    pendingUnreads.delete(contactId)
  })
  saveMarks()
}

// One communication of each conversation is being flipped to unread, counted in `inboxId`
export function addPendingUnreads (marks, inboxIds, now = Date.now()) {
  marks.forEach(({ contactId, inboxId }) => {
    pendingUnreads.set(contactId, { inboxIds, inboxId, at: now })
    pendingReads.delete(contactId)
  })
  saveMarks()
}

export function settlePendingRead (contactId) {
  pendingReads.delete(contactId)
  saveMarks()
}

export function settlePendingUnread (contactId) {
  pendingUnreads.delete(contactId)
  saveMarks()
}

// The conversation's counts came from master, or a live update changed it since it was marked
export function settlePendingMarks (contactId) {
  pendingReads.delete(contactId)
  pendingUnreads.delete(contactId)
  saveMarks()
}

export function pendingContactIds (now = Date.now()) {
  pruneMarks(now)

  return [...new Set([...pendingReads.keys(), ...pendingUnreads.keys()])]
}

// The badge count of each inbox in an unread count response, with the pending marks applied.
// The response must carry the unread_contact_<id> column of every pending conversation.
export function applyPendingMarks (rows, inboxIds) {
  const rowOf = inboxId => (rows || []).find(row => row.ring_group_id === inboxId)
  const counts = new Map(inboxIds.map(inboxId => [inboxId, Number(rowOf(inboxId)?.unread_count || 0)]))

  pendingReads.forEach(({ inboxIds: scope }, contactId) => {
    scope.filter(inboxId => counts.has(inboxId)).forEach(inboxId => {
      counts.set(inboxId, counts.get(inboxId) - contactCount(rowOf(inboxId), contactId))
    })
  })

  // the replica shows none of the conversation's unreads yet, so the flip is not in the count
  pendingUnreads.forEach(({ inboxIds: scope, inboxId }, contactId) => {
    const counted = scope.some(scopeInboxId => contactCount(rowOf(scopeInboxId), contactId) > 0)

    if (!counted && counts.has(inboxId)) {
      counts.set(inboxId, counts.get(inboxId) + 1)
    }
  })

  return new Map([...counts].map(([inboxId, count]) => [inboxId, Math.max(count, 0)]))
}

// Show the pending marks on list rows of a view over `inboxIds`. A read mark only clears
// rows when it covers every inbox of the view. Returns the conversations shown as read,
// so the caller can drop the rows its filters exclude.
export function applyPendingMarksToItems (items, inboxIds, now = Date.now()) {
  pruneMarks(now)

  const readContactIds = []

  items.forEach(item => {
    const read = pendingReads.get(item.contact_id)
    const unread = pendingUnreads.get(item.contact_id)

    if (read && inboxIds.every(inboxId => read.inboxIds.includes(inboxId))) {
      item.is_read = true
      item.inbox_unread_count = 0

      if (item.repeats > 0) {
        item.unread_repeats = 0
      }

      readContactIds.push(item.contact_id)
    } else if (unread && inboxIds.includes(unread.inboxId)) {
      item.is_read = false
      item.inbox_unread_count = Math.max(item.inbox_unread_count || 0, 1)
    }
  })

  return readContactIds
}

export function resetPendingMarks () {
  pendingReads.clear()
  pendingUnreads.clear()
  saveMarks()
}
